// TODAY'S VERSION (v1), kept beside the v1.1 one until the press (lib/release/next).
// Copied from main as it was on 2026-10-23; everyone who is not a test account sees this one.
// onboarding.ts — the pure state machine + script for the in-dashboard first-run
// "odyssey". No React, no I/O: importable by the server (data.ts reads the stored
// state) and the client conductor alike. Storage lives in `user_settings.onboarding`
// (migration 054); `user_metadata.onboarded` remains the terminal graduated bit.
//
// THE WALK (2026-09): a brand-new account arrives with the netti it was born
// with (the birth page minted it before the door — `netti` below carries it in).
// netti descends from the top of the room to its seat, introduces the spaces
// along the top, and then wakes them one at a time — CONNECTIONS FIRST, because
// nothing here means anything until one ai can read this memory. Then the
// project space: the connected tool creates the first folder on its own from a
// pasted prompt, and the person watches it arrive. Then `free` = everything
// unlocked, the account marked `onboarded`, and per-space first-visit intros
// fire after that, tracked in `seen`.
//
// The stage ENUM is unchanged (stored states stay parseable); only the ORDER
// and the unlock sets moved. `create` and `import` remain as parked stages —
// the conductor's NEXT map steps over them.

export const OB_VERSION = 1 as const;

export type ObStage =
  | "home"
  | "projects"
  | "create"
  | "connections"
  | "import"
  | "free";

/** The ordered spine of stages — used to compare progress + step forward.
 *  Connections come BEFORE projects: the first folder is made by the tool you
 *  just wired, not by hand. */
export const OB_STAGES: ObStage[] = [
  "home",
  "connections",
  "projects",
  "create",
  "import",
  "free",
];

/** The netti a person was born with on the birth page (/test/onboarding →
 *  the real signup). Carried into the account on first arrival so the creature
 *  under the dashboard is the SAME one they held into existence — never a second
 *  mint from their email. */
export interface ObNetti {
  /** the identity string the birth minted from (futureIdentity seed). */
  identity: string;
  /** THE PERSON'S name, not the creature's. The birth asks you to complete
   *  "this is ___'s netti", so what you type is what YOU are called here. It
   *  becomes the account's display name. Reading it as the netti's name is
   *  what once produced "bob is here, bob." */
  owner?: string;
}

export interface ObState {
  v: typeof OB_VERSION;
  /** the current stage of the flow. */
  stage: ObStage;
  /** index of the current script beat within the stage. */
  beat: number;
  /** panel key -> ISO timestamp of the first-visit intro being shown. */
  seen: Record<string, string>;
  /** the user skipped the "make your first project" step. */
  createSkipped?: boolean;
  /** when an import was kicked off (for the "building" affordance / resume). */
  importStartedAt?: string;
  startedAt: string;
  /** set when the flow reaches `free` (mirrors user_metadata.onboarded). */
  completedAt?: string;
  /** the user clicked "explore" — the free-stage greeting is done for good, so
   *  it can never re-narrate; only ambient per-space intros play from here. */
  dismissed?: boolean;
  /** netti's arrival (the descent + the spaces) has played. Until this is set, a
   *  brand-new account opens on the arrival rather than the dashboard; once
   *  set, the guided walk proper begins and the arrival never returns. */
  bornAt?: string;
  /** the walk was parked on a phone ("continue later on desktop"). bornAt stays
   *  unset so a desktop session replays the whole welcome from the arrival; a
   *  phone shows the home space with a quiet "finish on desktop" note instead
   *  of the tour. Cleared implicitly when the flow reaches `free`. */
  deferredAt?: string;
  /** the netti carried in from the birth page (see ObNetti). */
  netti?: ObNetti;
  /** the tool the person chose to wire first (a CONNECT_TOOLS id). */
  tool?: string;
}

/** Which spaces are unlocked at each stage. `"all"` = no locking. Keys are OsNode
 *  keys — panels (PanelKind) OR the "projects" section, so this is string-typed. */
export const UNLOCKS: Record<ObStage, string[] | "all"> = {
  // the `home` STAGE keeps its stored name (old states must stay parseable) but
  // the space it used to open on is gone — projects is where a person arrives,
  // so that is the first door the walk unlocks.
  home: ["projects"],
  connections: ["projects", "connections"],
  projects: ["projects", "connections"],
  create: ["projects", "connections"],
  import: ["projects", "connections"],
  free: "all",
};

/** Each onboarding stage wears its own colour, so the first-run is a journey
 *  through the spectrum — netti blooms and every space glows a different hue —
 *  rather than one flat white screen. The hue feeds a ColorState in OsShell that
 *  drives the bloom + retints the whole UI for that stage. `free` = the user's
 *  own organism takes back over.
 *
 *  When the account carries a birth netti (`ObState.netti`) the shell keeps THAT
 *  creature's palette throughout and these hues are not used — the walk is in
 *  the colour they were born with. */
export const STAGE_HUE: Record<ObStage, number> = {
  home: 217, // netti blue — the bond
  projects: 268, // violet — the ideas space
  create: 30, // amber — making a thing
  connections: 168, // teal — wiring in
  import: 320, // magenta
  free: 140, // green — alive, settled
};

/** Per-space hue for the post-graduation first-visit intros (keyed by panel key),
 *  so opening a space for the first time also arrives in its own colour. */
export const SPACE_HUE: Record<string, number> = {
  projects: 268,
  skills: 96,
  connections: 168,
  // a soft rose, apart from you (322) and activity (24): the one hue no other space wears
  agents: 352,
  you: 322,
  netti: 200,
  // a deep sea blue, between netti (200) and the projects' violet: the memory itself
  memory: 228,
  activity: 24,
  tasks: 140,
  parked: 52,
  settings: 250,
};

/** A fresh state for an account entering the flow. `now` is passed in (no clock in a pure module). */
export function initialObState(now: string): ObState {
  return { v: OB_VERSION, stage: "home", beat: 0, seen: {}, startedAt: now };
}

/**
 * The set of unlocked panel keys for a stored state, or `null` when everything is
 * unlocked (no state, a completed flow, or an unknown/old version). `null` is the
 * signal buildRoot uses to skip locking entirely — backward-compatible by default.
 */
export function unlockedFor(s: ObState | null | undefined): ReadonlySet<string> | null {
  if (!s || s.v !== OB_VERSION) return null;
  const u = UNLOCKS[s.stage];
  if (u === "all") return null;
  return new Set<string>(u);
}

/** Is the flow still in progress (i.e. the conductor should run + locking applies)? */
export function isOnboarding(s: ObState | null | undefined): boolean {
  return !!s && s.v === OB_VERSION && s.stage !== "free";
}

/** The next stage after `stage`, or `stage` itself if already at the end. */
export function nextStage(stage: ObStage): ObStage {
  const i = OB_STAGES.indexOf(stage);
  return i < 0 || i >= OB_STAGES.length - 1 ? "free" : OB_STAGES[i + 1];
}

/** Coerce an unknown value read from the DB into a valid ObState or null. */
export function parseObState(raw: unknown): ObState | null {
  if (!raw || typeof raw !== "object") return null;
  const r = raw as Record<string, unknown>;
  if (r.v !== OB_VERSION) return null;
  if (typeof r.stage !== "string" || !OB_STAGES.includes(r.stage as ObStage)) return null;
  const n = r.netti && typeof r.netti === "object" ? (r.netti as Record<string, unknown>) : null;
  const netti: ObNetti | undefined =
    n && typeof n.identity === "string" && n.identity
      ? {
          identity: n.identity,
          // `name` is the old key for the same value, kept readable so a state
          // stored before the rename still knows who its person is.
          ...(() => {
            const o = typeof n.owner === "string" && n.owner ? n.owner : typeof n.name === "string" ? n.name : "";
            return o ? { owner: o.slice(0, 40) } : {};
          })(),
        }
      : undefined;
  return {
    v: OB_VERSION,
    stage: r.stage as ObStage,
    beat: typeof r.beat === "number" ? r.beat : 0,
    seen: r.seen && typeof r.seen === "object" ? (r.seen as Record<string, string>) : {},
    createSkipped: r.createSkipped === true || undefined,
    importStartedAt: typeof r.importStartedAt === "string" ? r.importStartedAt : undefined,
    startedAt: typeof r.startedAt === "string" ? r.startedAt : "",
    completedAt: typeof r.completedAt === "string" ? r.completedAt : undefined,
    dismissed: r.dismissed === true || undefined,
    bornAt: typeof r.bornAt === "string" ? r.bornAt : undefined,
    deferredAt: typeof r.deferredAt === "string" ? r.deferredAt : undefined,
    ...(netti ? { netti } : {}),
    ...(typeof r.tool === "string" && r.tool ? { tool: r.tool.slice(0, 40) } : {}),
  };
}

// ── the script ────────────────────────────────────────────────────────────────
//
// Each beat is one line netti says in the guide. `advance` names how the beat is
// dismissed:
//   "next"   — the "next" chip.
//   "hover"  — the user hovers anything (a hint fires) — teaches the Noticer.
//   "action" — a labelled action chip drives it (e.g. "claude", "copy the prompt").
//   "auto"   — an external event advances it (a connection lands, a project arrives).
// `actions` are extra labelled chips beyond the default "next". There are NO
// skip chips: the one quiet exit on every beat is "enter innernet" (act `enter`),
// which opens every space at once.
// `org` is an optional organism override key (the conductor maps it to a menuOrg-style choreo).
//
// `{name}` fills with the person's first name and `{tool}` with the tool they
// chose to wire.

export type ObAdvance = "next" | "hover" | "action" | "auto";

export interface ObAction {
  label: string;
  /** a semantic id the conductor interprets (goto:/projects, connect:<tool>, copy-project-prompt, skip-step, unlock:free …). */
  act: string;
}

export interface ObBeat {
  id: string;
  text: string;
  advance: ObAdvance;
  actions?: ObAction[];
  /** organism override key for this beat (conductor-mapped). */
  org?: string;
  /** show only on this viewport (default: both). */
  only?: "mobile" | "desktop";
}

/** The beats per stage, in order. The conductor plays the current stage's beats. */
export const SCRIPT: Record<Exclude<ObStage, "free">, ObBeat[]> = {
  home: [
    // spoken the moment the arrival hands over: netti is already in its seat.
    // One line, then the first door.
    // ONE door, said once. The first thing a new account is offered is the
    // connection itself, not a tour of empty rooms: pressing it wakes the
    // connections space, opens it, and netti steps to the corner to guide.
    { id: "home.wake", text: "this is home. your spaces run along the top. nothing here means anything until one ai can read your memory, so let's make your first connection. you paste a link, never a key.", advance: "action", org: "home-intro", actions: [{ label: "create a connection", act: "connect:any" }] },
  ],
  connections: [
    // the space wakes and slides itself into view (the conductor auto-navigates).
    // The chips ARE the choice of tool: tapping one opens the guided wire-up.
    { id: "conn.woke", text: "every ai you use gets its own door into this memory. which one do you talk to most? i'll walk you through it. you paste a link, never a key.", advance: "action", org: "connections-regrow", actions: [
      { label: "claude", act: "connect:claude-desktop" },
      { label: "chatgpt", act: "connect:chatgpt" },
      { label: "cursor", act: "connect:cursor" },
      { label: "claude code", act: "connect:claude-code" },
      { label: "another tool", act: "connect:any" },
    ] },
    { id: "conn.mobile", text: "wiring a tool is awkward on a phone. everything here is saved. pick this up on your desktop, or step in and look around first.", advance: "action", only: "mobile", actions: [{ label: "enter innernet", act: "enter" }] },
    { id: "conn.wiring", text: "take your time. the moment {tool} calls through its link, i'll know.", advance: "auto" },
    // a connection standing is the whole of the walk: from here every space
    // opens at once (`enter`), and the first folder arrives on its own the
    // first time the wired tool is asked about a project.
    { id: "conn.connected", text: "{tool} just called through. it can read your memory now, and everything it keeps from here lands with me. everything else is open.", advance: "action", actions: [{ label: "enter innernet", act: "enter" }] },
    // the tool got here FIRST — the account was made from inside it (the consent
    // page) or while pairing the terminal, so a connection was already standing
    // when the walk began. Nothing to wire; the stage greets what is here.
    { id: "conn.already", text: "an ai already called through before you did. it can read your memory now, and everything it keeps from here lands with me. its door is on this shelf whenever you want to name it.", advance: "action", actions: [{ label: "enter innernet", act: "enter" }] },
    // the tool that sent them here is still WAITING AT THE DOOR: they made the
    // account inside it, met their netti, and came straight here. Letting it in
    // finishes that authorization and returns them to it; waving it off keeps
    // the walk going and the tool asks again on its own.
    { id: "conn.waiting", text: "{tool} is at the door, waiting to be let in. say yes and it reads this memory from now on, and you go back to it.", advance: "action", actions: [
      { label: "let it in", act: "grant" },
      { label: "not now", act: "grant-later" },
    ] },
  ],
  projects: [
    // the first folder is made by the tool, not by hand: paste the prompt, watch
    // it arrive. The conductor advances on the project count rising.
    { id: "projects.woke", text: "one folder per thing you are building, each kept current on its own. paste this prompt into {tool} and the first folder appears here by itself.", advance: "auto", actions: [{ label: "copy the prompt", act: "copy-project-prompt" }] },
    { id: "projects.made", text: "there it is. tap it to see how {tool} organised what it already knew about you. everything else opens when you are ready.", advance: "action", actions: [{ label: "enter innernet", act: "enter" }] },
  ],
  create: [
    { id: "create.ask", text: "let's make your first folder. name a thing you're building or thinking about.", advance: "action", actions: [{ label: "try", act: "create" }] },
    { id: "create.made", text: "there it is. the auto toggle means netti keeps its description fresh as memory lands. switch it off to write your own.", advance: "next" },
  ],
  import: [
    { id: "import.offer", text: "your other ais already know you. bring that memory here: paste a prompt into any chat, or upload your export when it arrives.", advance: "action", actions: [{ label: "import", act: "import" }, { label: "later", act: "enter" }] },
    { id: "import.building", text: "building your memory. you can wander, i'll keep at it.", advance: "next" },
    { id: "import.settings", text: "exports take about a day to arrive. this lives in memory → import whenever you're ready.", advance: "action", actions: [{ label: "finish", act: "enter" }] },
  ],
};

// ── per-space first-visit intros (post-free) ────────────────────────────────────
// Seeded from the SPACES one-liners + panel summaries. Keyed by PanelKind.

export const SPACE_INTROS: Record<string, string> = {
  projects: "every context map you build. one folder per idea, product or brand.",
  connections: "the ai tools reading and growing this same memory, live.",
  agents: "agents you build and tools you talk to, and what each has learned about you.",
  you: "who you are. your people, your tools, everything innernet holds for you.",
  netti: "the living body of your memory. watch it, and shape the ground it lives on.",
  memory: "your whole memory at a glance: how big it is, what reached it lately, and bringing more in.",
  activity: "everything touched, narrated. what your ais did, and what innernet did on its own.",
  tasks: "every open loop across all your projects, one running checklist.",
  skills: "what you can teach an ai. every page you keep that carries a skill, each one a link you can hand out.",
  parked: "ideas set aside on a branch. out of the way, still here.",
  settings: "your account, security, and the data innernet keeps for you.",
};

// ── the ground, handed over ──────────────────────────────────────────────────
// A new person's ground is tinted from the netti they were born with (bgId
// "minted", lib/theme mintedGround). Settings is where netti tells them, once,
// that the colour is theirs to change, with THE dial one press away (act
// `ground` → GROUND_DIAL_EVENT; never a second picker). Someone who has already
// picked a colour of their own hears the plain settings line instead.
export const SETTINGS_GROUND_INTRO: { text: string; actions: ObAction[] } = {
  text: "your account, and the data innernet keeps for you. your ground was tinted from your netti. please change it whenever you like.",
  actions: [{ label: "change colour", act: "ground" }],
};

/** What netti says on a first visit to a space (post-free), or null. */
export function spaceIntro(
  key: string,
  opts: { mintedGround?: boolean } = {},
): { text: string; actions?: ObAction[] } | null {
  if (key === "settings" && opts.mintedGround) return SETTINGS_GROUND_INTRO;
  const text = SPACE_INTROS[key];
  return text ? { text } : null;
}
